import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { FirebaseService } from './firebase.service';
import { mergeMap } from 'rxjs';
import { NewLicenseProduct } from './machines.service';

export type RegistrationDbUpdateLogRow = {
  id: string;
  created_at: string;
  user_email: string;
  account_number: string;
  action: string;
  unique_id: string;
  computer_name: string;
  guid: string;
  features: string;
};

type RegistrationDbLogsBaseQuery = {
  accountNumber?: string;
  createdBy?: string;
  createdAfter?: string;
  createdBefore?: string;
};

export type RegistrationDbLogsQuery = RegistrationDbLogsBaseQuery & {
  pageNumber: number;
  pageSize: number;
};

export type RegistrationDbLogsExportQuery = RegistrationDbLogsBaseQuery;

export type RegistrationDbLogsResponse = {
  rows: RegistrationDbUpdateLogRow[];
  total_count: number;
  page_number: number;
  page_size: number;
  total_pages: number;
};

export type NewLicenseProductCatalog = {
  primaries: NewLicenseProduct[];
  addons: NewLicenseProduct[];
  manifests: NewLicenseProduct[];
};

export type CreateNewDesktopLicenseRequest = {
  account: string;
  primary_code: string;
  addon_codes: string[];
  manifest_codes: string[];
  expiration_date: string;
};

export type CreateNewDesktopLicenseResponse = {
  success: boolean;
  message: string;
  data: {
    registration_id: number;
  };
};

export type CreateNewDesCreditResponse = {
  success: boolean;
  message: string;
  data: {
    registration_id: number;
  };
};

@Injectable({
  providedIn: 'root',
})
export class RegistrationDbService {
  private baseUrl = environment.serverUrl + '/registration-db';

  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService
  ) {}

  deactivateRecord(accountNumber: string, registrationId: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<{ success: boolean }>(
          `${this.baseUrl}/desktop-license/deactivate`,
          {
            account: accountNumber,
            registration_id: registrationId,
          },
          {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          }
        );
      })
    );
  }

  activateRecord(accountNumber: string, registrationId: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<{ success: boolean }>(
          `${this.baseUrl}/desktop-license/activate`,
          {
            account: accountNumber,
            registration_id: registrationId,
          },
          {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          }
        );
      })
    );
  }

  deleteNewDesktopLicenseRecord(accountNumber: string, registrationId: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<{ success: boolean }>(
          `${this.baseUrl}/desktop-license/delete`,
          {
            account: accountNumber,
            registration_id: registrationId,
          },
          {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          }
        );
      })
    );
  }

  getNewDesktopLicenseProductList() {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get<NewLicenseProductCatalog>(
          `${this.baseUrl}/desktop-license/product-list`,
          {
            headers: { Authorization: 'Bearer ' + token },
          }
        );
      })
    );
  }

  updateDesCredit(
    accountNumber: string,
    registrationId: number,
    credit: number
  ) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<{ success: boolean }>(
          `${this.baseUrl}/des-credits/update`,
          {
            account: accountNumber,
            registration_id: registrationId,
            credit,
          },
          {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          }
        );
      })
    );
  }

  updateDesCreditApiKeyLink(
    accountNumber: string,
    registrationId: number,
    linkedToApiKey: boolean
  ) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<{ success: boolean }>(
          `${this.baseUrl}/des-credits/api-key-link`,
          {
            account: accountNumber,
            registration_id: registrationId,
            linked_to_api_key: linkedToApiKey,
          },
          {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          }
        );
      })
    );
  }

  downloadDesCreditDat(accountNumber: string, registrationId: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.get(`${this.baseUrl}/des-credits/dat`, {
          params: {
            account: accountNumber,
            registration_id: String(registrationId),
          },
          headers: { Authorization: 'Bearer ' + token },
          responseType: 'blob',
        });
      })
    );
  }

  createDesCredit(accountNumber: string, credit: number) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<CreateNewDesCreditResponse>(
          `${this.baseUrl}/des-credits/create`,
          {
            account: accountNumber,
            credit,
          },
          {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          }
        );
      })
    );
  }

  createNewDesktopLicense(req: CreateNewDesktopLicenseRequest) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        return this.http.post<CreateNewDesktopLicenseResponse>(
          `${this.baseUrl}/desktop-license/create`,
          req,
          {
            headers: {
              Authorization: 'Bearer ' + token,
              'Content-Type': 'application/json',
            },
          }
        );
      })
    );
  }

  getUpdateLogs(query: RegistrationDbLogsQuery) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const params = new URLSearchParams({
          page_number: String(query.pageNumber),
          page_size: String(query.pageSize),
        });
        this.appendLogFilters(params, query);
        return this.http.get<RegistrationDbLogsResponse>(
          `${this.baseUrl}/logs?${params.toString()}`,
          {
            headers: { Authorization: 'Bearer ' + token },
          }
        );
      })
    );
  }

  exportUpdateLogs(query: RegistrationDbLogsExportQuery) {
    return this.firebaseService.idToken().pipe(
      mergeMap((token) => {
        const params = new URLSearchParams({ format: 'csv' });
        this.appendLogFilters(params, query);
        return this.http.get(`${this.baseUrl}/logs?${params.toString()}`, {
          headers: { Authorization: 'Bearer ' + token },
          responseType: 'blob',
        });
      })
    );
  }

  private appendLogFilters(
    params: URLSearchParams,
    query: Omit<RegistrationDbLogsQuery, 'pageNumber' | 'pageSize'>
  ) {
    const acct = query.accountNumber?.trim();
    if (acct) {
      params.set('account_number', acct);
    }
    const by = query.createdBy?.trim();
    if (by) {
      params.set('created_by', by);
    }
    if (query.createdAfter) {
      params.set('created_after', query.createdAfter);
    }
    if (query.createdBefore) {
      params.set('created_before', query.createdBefore);
    }
  }
}
